import {
  PushState,
  readThreadMessage,
  type AssistantMessageDto,
  type AssistantMessagePart,
  type AssistantThreadMessage,
  type AssistantTestPurchaseDto,
  type ChargeQuestionCountsDto,
  type TelemetryAggregateDto,
  type TelemetryDayRowDto,
} from '@signalvote/shared';

const NONE = '—';

export function formatMs(ms: number | null | undefined): string {
  if (ms == null) return NONE;
  return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`;
}

export function formatCost(usd: number | null | undefined): string {
  return usd == null ? NONE : `$${usd.toFixed(4)}`;
}

export function formatCount(n: number | null | undefined): string {
  return n == null ? NONE : n.toLocaleString('en-US');
}

export function answeredLabel(counts: ChargeQuestionCountsDto): string {
  if (counts.asked === 0) return 'none asked';
  return `${formatCount(counts.answered)} of ${formatCount(counts.asked)} (${Math.round((counts.answered / counts.asked) * 100)}%)`;
}

/** A run instant in its campaign's timezone; labeled UTC when that timezone is unknown. */
export function formatInstant(iso: string | null | undefined, timeZoneId: string | undefined): string {
  if (iso == null) return NONE;
  const text = new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timeZoneId ?? 'UTC',
  });
  return timeZoneId ? text : `${text} UTC`;
}

/** "UserMessage 3 · Event 1": the nonzero counts of a by-trigger or by-outcome tally. */
export function countsLabel(counts: Record<string, number>): string {
  const nonzero = Object.entries(counts).filter(([, n]) => n > 0);
  return nonzero.length === 0 ? NONE : nonzero.map(([name, n]) => `${name} ${n}`).join(' · ');
}

export function pushesLabel(a: TelemetryAggregateDto): string {
  return `${a.pushesSent} / ${a.pushesFailed} / ${a.pushesDeferred}`;
}

export interface DayGroup {
  localDay: string;
  rows: TelemetryDayRowDto[];
}

/** Newest day first; campaigns by name within a day. */
export function groupDays(rows: TelemetryDayRowDto[]): DayGroup[] {
  const byDay = new Map<string, TelemetryDayRowDto[]>();
  for (const row of rows) {
    const list = byDay.get(row.localDay) ?? [];
    list.push(row);
    byDay.set(row.localDay, list);
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([localDay, list]) => ({
      localDay,
      rows: [...list].sort((a, b) => a.campaignName.localeCompare(b.campaignName)),
    }));
}

/** The campaign filter's choices: each campaign that has runs or pushes in the overview, once, by name. */
export function campaignOptions(rows: TelemetryDayRowDto[]): { value: string; label: string }[] {
  const names = new Map(rows.map((row) => [row.campaignId, row.campaignName]));
  return [...names.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export type ToolStatus = 'ok' | 'error' | 'no result';

export type ConversationRow =
  | { kind: 'text'; key: string; role: string; text: string }
  | { kind: 'tool'; key: string; name: string; status: ToolStatus }
  | { kind: 'moment'; key: string; momentKind: string; text: string | null };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const textOf = (part: AssistantMessagePart): string =>
  part.type === 'text' && typeof part.text === 'string' ? part.text.trim() : '';

function toolStatus(part: AssistantMessagePart): ToolStatus {
  if (part.state === 'output-error') return 'error';
  if (part.state !== 'output-available') return 'no result';
  return isRecord(part.output) && 'error' in part.output ? 'error' : 'ok';
}

function partRow(message: AssistantThreadMessage, part: AssistantMessagePart, index: number): ConversationRow | null {
  const key = `${message.id}:${index}`;
  const text = textOf(part);
  if (text !== '') return { kind: 'text', key, role: message.role, text };
  if (part.type.startsWith('tool-')) {
    return { kind: 'tool', key, name: part.type.slice('tool-'.length), status: toolStatus(part) };
  }
  return null;
}

/** A moment (first part `data-moment`) collapses to one card; otherwise text and tool-call parts become rows. */
export function conversationRows(messages: AssistantMessageDto[]): ConversationRow[] {
  return messages.map(readThreadMessage).flatMap((message): ConversationRow[] => {
    const [first] = message.parts;
    if (first?.type === 'data-moment') {
      const data = first.data;
      const rendered = message.parts.map(textOf).filter((t) => t !== '');
      return [
        {
          kind: 'moment',
          key: `${message.id}:moment`,
          momentKind: isRecord(data) && typeof data.kind === 'string' ? data.kind : 'Moment',
          text: rendered.length > 0 ? rendered.join('\n\n') : null,
        },
      ];
    }
    return message.parts.flatMap((part, i) => partRow(message, part, i) ?? []);
  });
}

/** The bench purchase's result in plain words: who was asked, whether the push went, then the server's warnings. */
export function purchaseOutcomeLines(result: AssistantTestPurchaseDto, memberUserId: string): string[] {
  const asked = result.askedUserIds.length;
  const lines = [
    asked === 0
      ? 'In the ledger. Nobody was asked.'
      : `In the ledger. Asked ${asked} ${asked === 1 ? 'person' : 'people'} now, ${
          result.askedUserIds.includes(memberUserId) ? 'this member among them' : 'not this member'
        }.`,
  ];
  if (result.push?.state === PushState.Sent) lines.push(`Pushed to this member's phones: Expo took ${result.push.ticketIds.length}.`);
  return [...lines, ...result.warnings];
}
